import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Injectable,
    type Provider,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    type MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserBuildingStore } from '@quipex/core/auth';
import {
    DIALOG_LIFECYCLE,
    type DialogLifecycleHandle,
    UNSAVED_CHANGES_ACCEPTED,
} from '@quipex/core/primitives';
import { ConfirmationDialog } from '@quipex/shared/ui';
import { take, takeUntil } from 'rxjs';

interface AccessCheckContext {
    blocked(): boolean;
    checking(): boolean;
    retry(): void;
    leave(): void;
}
@Component({
    imports: [ConfirmationDialog],
    templateUrl: './building-dialog-access-check.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
class BuildingDialogAccessCheck {
    protected readonly context = inject<AccessCheckContext>(MAT_DIALOG_DATA);
}

@Injectable()
class BuildingDialogLifecycle {
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly store = inject(UserBuildingStore);
    private readonly router = inject(Router);
    private readonly dialogs = signal<ReadonlySet<DialogLifecycleHandle>>(
        new Set()
    );
    private barrier: MatDialogRef<BuildingDialogAccessCheck> | null = null;
    private readonly context: AccessCheckContext = {
        blocked: computed(() => this.store.accessDecision() !== 'granted'),
        checking: computed(() => this.store.accessDecision() !== 'unavailable'),
        retry: () => this.store.reload(),
        leave: () =>
            void this.router.navigate(['/buildings'], {
                info: UNSAVED_CHANGES_ACCEPTED,
            }),
    };

    constructor() {
        effect(() => {
            const dialogs = this.dialogs();
            const blocked = this.context.blocked();
            for (const dialog of dialogs)
                dialog.element?.toggleAttribute('inert', blocked);
            if (dialogs.size && blocked && !this.barrier) {
                this.barrier = this.dialog.open(BuildingDialogAccessCheck, {
                    data: this.context,
                    disableClose: true,
                    ariaModal: true,
                    ariaLabel: 'Building access verification',
                    autoFocus: 'first-heading',
                    restoreFocus: true,
                    panelClass: ['qpx-dialog', 'sm', 'confirm', 'auto-h'],
                });
            } else if ((!blocked || !dialogs.size) && this.barrier) {
                this.barrier.close();
                this.barrier = null;
            }
        });
    }

    watch(dialog: DialogLifecycleHandle): void {
        this.dialogs.update((dialogs) => new Set([...dialogs, dialog]));
        this.store.accessEnded$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(dialog.closed$)
            )
            .subscribe(() => {
                if (dialog.hasUnsavedChanges())
                    this.store.discardUnsavedChangesAfterAccessLoss();
                dialog.close();
            });
        dialog.closed$.pipe(take(1)).subscribe(() => {
            this.dialogs.update(
                (dialogs) =>
                    new Set(
                        [...dialogs].filter((current) => current !== dialog)
                    )
            );
        });
    }
}

export function provideBuildingDialogLifecycle(): Provider[] {
    return [
        BuildingDialogLifecycle,
        {
            provide: DIALOG_LIFECYCLE,
            useFactory: () => {
                const lifecycle = inject(BuildingDialogLifecycle);
                return (dialog: DialogLifecycleHandle) =>
                    lifecycle.watch(dialog);
            },
        },
    ];
}
